/** @file attachment.set: changes an attachment's grouping, visibility condition and driver. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Attachment } from '@zilo/pose3d-format/types/puppet';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { checkAttachmentRefs } from './checkAttachmentRefs';
import { readAttachmentFields } from './readAttachmentFields';

/**
 * `attachment.set { id, fields }` → `{}`: merges `weaponGroup`, `when`, `driver` (their host nodes must exist);
 * `nodeMap` cannot change after flattening (re-attach with `replace`). Inverse: the previous values, else P.
 */
export const attachmentSetCommand: CommandDef<'attachment.set'> = {
  name: 'attachment.set',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['id', 'fields']);
    const f = readInput(r.raw('fields'), r.at('fields'), ['weaponGroup', 'when', 'driver', 'nodeMap']);
    if (f.has('nodeMap')) failField(f.at('nodeMap'), 'no nodeMap', 'nodeMap', 'nodeMap is fixed once flattened; attachment.attach with replace: true');
    return { id: r.str('id'), fields: compact(readAttachmentFields(f)) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const a = (draft.puppet.attachments ?? []).find((x) => x.id === input.id)
      ?? throwError(ERROR_CODES.NOT_FOUND, { id: input.id }, { path: '/input/id', hint: 'an attachment id of the puppet' });
    const keys = Object.keys(input.fields) as (keyof Attachment)[];
    const inverse = keys.every((k) => a[k] !== undefined)
      ? [{ name: 'attachment.set' as const, input: { id: input.id, fields: structuredClone(Object.fromEntries(keys.map((k) => [k, a[k]]))) } }]
      : puppetInverse(draft);
    Object.assign(a, structuredClone(input.fields));
    checkAttachmentRefs(draft.puppet, a, '/input/fields');
    checkPuppet(draft, ctx);
    return { result: {}, warnings: [], inverse };
  },
};
